import { analyticsQueryTables } from "schemas/analyticsRead";
import {
  INSIGHTS_PROXY_PROVIDERS,
  INSIGHTS_PROXY_VIEW_NAMES_BY_PROVIDER,
  sqlReferencesViewName,
} from "schemas/bedrockApi/insightsProxy";
import {
  insightsComponents,
  tabbedBarListTabSchema,
} from "schemas/insightsDashboard/catalog";
import {
  dashboardScopeDefaultsSchema,
  insightsDatasetSchema,
} from "schemas/insightsDashboard/scope";
import { z } from "zod";

export const INSIGHTS_DOCUMENT_VERSION = "2.0";

const READONLY_QUERY_PATTERN = /^\s*(select|with)\b/i;

export const insightsFileNameSchema = z
  .string()
  .min(1)
  .max(120)
  .regex(/^[\da-z][\d._a-z-]*\.insights\.json$/, {
    message: "Filenames must be lowercase and end in .insights.json.",
  });

// NOTE (Berkley, 2026-07-30): pixel usage is detected via the project-scoped
// public analytics tables (events_computed + the daily rollups) so legacy
// blended joins infer both sides instead of silently dropping pixel filters.
const PIXEL_TABLES = analyticsQueryTables.filter((table) => {
  return table.public && table.projectScoped;
});

const PIXEL_VIEWS = PIXEL_TABLES.map((table) => table.name);

// NOTE (Berkley, 2026-08-03): comments and string literals are both blanked
// before matching so a view name mentioned in either can't flip a query's
// datasets. Literals have to be matched first so a `--` inside one isn't read
// as the start of a comment.
function stripSqlCommentsAndStringLiterals(sql: string): string {
  return sql.replace(/'(?:[^'\\]|\\.|'')*'|--[^\n]*|\/\*[\s\S]*?\*\//g, " ");
}

// Datasets are always derived from the SQL — pixel and/or the providers whose
// views it references, defaulting to pixel when nothing matches — so a query's
// classification can never drift from what it actually reads. Derived at
// compile time, never stored in the document.
export function inferQueryDatasets(
  sql: string,
): z.infer<typeof insightsDatasetSchema>[] {
  const bare = stripSqlCommentsAndStringLiterals(sql);
  const providers = INSIGHTS_PROXY_PROVIDERS.filter((provider) => {
    return INSIGHTS_PROXY_VIEW_NAMES_BY_PROVIDER[provider].some((view) => {
      return sqlReferencesViewName(bare, view);
    });
  });
  const referencesPixel = PIXEL_VIEWS.some((view) => {
    return sqlReferencesViewName(bare, view);
  });
  if (referencesPixel || providers.length === 0) {
    return ["pixel", ...providers];
  }
  return providers;
}

export interface QueryTimestampColumn {
  name: string;
  /** `Date` columns carry no time of day, so they cannot bucket finer than a day. */
  isDateOnly: boolean;
}

// `events_computed` dates rows by `date` and the daily rollups by `day`, so a
// date expression is only valid on the tables a query actually reads.
const TIMESTAMP_COLUMNS_BY_PIXEL_TABLE = new Map<string, QueryTimestampColumn>(
  PIXEL_TABLES.flatMap((table) => {
    const column = table.columns.find((candidate) => {
      return candidate.name === table.timestampColumn;
    });
    return column
      ? ([
          [
            table.name,
            { isDateOnly: column.type === "Date", name: column.name },
          ],
        ] as const)
      : [];
  }),
);

/** Timestamp columns of the pixel tables a query reads; more than one means it joins tables that date rows differently. */
export function inferQueryTimestampColumns(
  sql: string,
): QueryTimestampColumn[] {
  const bare = stripSqlCommentsAndStringLiterals(sql);
  const columnsByName = new Map<string, QueryTimestampColumn>();
  for (const [table, column] of TIMESTAMP_COLUMNS_BY_PIXEL_TABLE) {
    if (sqlReferencesViewName(bare, table)) {
      columnsByName.set(column.name, column);
    }
  }
  return [...columnsByName.values()];
}

const inlineSqlSchema = z
  .object({
    sql: z
      .string()
      .min(1)
      .max(8000)
      .refine((sql) => READONLY_QUERY_PATTERN.test(sql.trim()), {
        message: "Only SELECT and WITH (CTE) queries are allowed.",
      }),
  })
  .strict();

const queryIdentifierSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-z]\w*$/i, {
    message: "Query ids must be camelCase or snake_case identifiers.",
  });

const sharedQueryRefSchema = z
  .object({
    ref: queryIdentifierSchema,
  })
  .strict();

const dataBindingSchema = z.union([inlineSqlSchema, sharedQueryRefSchema]);

export type DataBinding = z.infer<typeof dataBindingSchema>;

export interface InsightsNode {
  kind: string;
  span?: number;
  data?: DataBinding;
  tabs?: ({ data: DataBinding } & Record<string, unknown>)[];
  children?: InsightsNode[];
  [prop: string]: unknown;
}

type ObjSchema = z.ZodObject<z.ZodRawShape>;

const components = insightsComponents;

const insightsNodeSchema: z.ZodType<InsightsNode> = z.lazy(() => {
  const [firstVariant, ...remainingVariants] = variants;
  if (!firstVariant) {
    return z.never();
  }
  // eslint-disable-next-line replo/no-as-cast -- NOTE (Ezra, 2026-07-28): Zod cannot infer this recursive runtime-derived union.
  return z.discriminatedUnion("kind", [
    firstVariant,
    ...remainingVariants,
  ]) as unknown as z.ZodType<InsightsNode>;
});

const nodeSpanSchema = z.number().int().min(1).max(12);
const childrenField = { children: z.array(insightsNodeSchema).default([]) };

const tabbedBarListTab = tabbedBarListTabSchema
  .omit({ queryId: true })
  .extend({ data: dataBindingSchema })
  .strict();

const variants = Object.entries(components).map(([kind, def]) => {
  let props: ObjSchema = def.props;
  if (kind === "Grid") props = props.omit({ spans: true });
  if (kind === "TabbedBarList") {
    props = props.extend({ tabs: z.array(tabbedBarListTab).min(1) });
  } else if ("queryId" in props.shape) {
    props = props.omit({ queryId: true }).extend({ data: dataBindingSchema });
  }
  if ([...def.slots].includes("default")) props = props.extend(childrenField);
  return props
    .extend({ kind: z.literal(kind), span: nodeSpanSchema.optional() })
    .strict();
});

export const insightsDocumentSchema = z
  .object({
    version: z.literal(INSIGHTS_DOCUMENT_VERSION),
    title: z.string().min(1).max(120),
    subtitle: z.string().max(200).optional(),
    scope: dashboardScopeDefaultsSchema.optional(),
    queries: z.record(queryIdentifierSchema, inlineSqlSchema).optional(),
    body: z.array(insightsNodeSchema).min(1),
  })
  .strict();

export type InsightsDocument = z.infer<typeof insightsDocumentSchema>;
